"use client";

import { useState, useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Image from "next/image";

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  priceUnit: string;
  image: string;
  alt: string;
  description: string;
  tags: string[];
  badge?: string;
  badgeColor?: string;
  featured?: boolean;
}

const products: Product[] = [
  {
    id: "prod-1",
    name: "Drago Canario Escultura",
    category: "Arte",
    price: 89,
    priceUnit: "€",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae4be366-1772803198799.png",
    alt: "Escultura 3D del drago canario en resina blanca con acabado mate premium",
    description: "Réplica artística del árbol símbolo de Canarias. Impresa en resina de alta calidad con acabado mate.",
    tags: ["Resina", "Hecho a mano", "Canarias"],
    badge: "Más vendido",
    badgeColor: "#C4845A",
    featured: true,
  },
  {
    id: "prod-2",
    name: "Maqueta Arquitectónica A3",
    category: "Arquitectura",
    price: 240,
    priceUnit: "€",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a8cec4e8-1772803195305.png",
    alt: "Maqueta arquitectónica en escala 1:100 de edificio residencial con detalles de fachada",
    description: "Maqueta de presentación en escala 1:100. Ideal para proyectos residenciales y comerciales en Canarias.",
    tags: ["PLA", "Escala 1:100", "Profesional"],
    badge: "Nuevo",
    badgeColor: "#C4845A",
  },
  {
    id: "prod-3",
    name: "Set Decoración Volcánica",
    category: "Decoración",
    price: 65,
    priceUnit: "€",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1861a6d4e-1772803191759.png",
    alt: "Set de tres piezas decorativas con textura volcánica inspiradas en el Teide",
    description: "Set de 3 piezas con textura volcánica. Inspiradas en la geología única de las Islas Canarias.",
    tags: ["PLA Negro", "Set x3", "Decoración"],
  },
  {
    id: "prod-4",
    name: "Porta Retratos Geométrico",
    category: "Decoración",
    price: 38,
    priceUnit: "€",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_189ca8a60-1772803190313.png",
    alt: "Porta retratos con diseño geométrico hexagonal en filamento PLA color arena",
    description: "Diseño minimalista con formas geométricas. Compatible con fotos 10x15 cm. Varios colores disponibles.",
    tags: ["PLA", "10x15cm", "Minimalista"],
  },
  {
    id: "prod-5",
    name: "Prototipo Técnico Custom",
    category: "Personalizados",
    price: 0,
    priceUnit: "desde",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18cac0ad4-1772803191957.png",
    alt: "Pieza mecánica de precisión impresa en 3D con material PETG gris para uso industrial",
    description: "Prototipos funcionales con tolerancias de hasta 0.05mm. Materiales: PLA, PETG, ABS, Resina.",
    tags: ["PETG", "Precisión", "Bajo pedido"],
    badge: "Consultar",
    badgeColor: "#C4845A",
  },
  {
    id: "prod-6",
    name: "Figura Personalizada",
    category: "Personalizados",
    price: 120,
    priceUnit: "€ desde",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d001adb6-1772803192331.png",
    alt: "Figura personalizada en miniatura basada en fotografía de cliente con acabado pintado a mano",
    description: "Tu figura personalizada basada en foto. Proceso 3D scan + modelado + impresión + pintado a mano.",
    tags: ["Resina", "Pintado a mano", "Único"],
    badge: "Exclusivo",
    badgeColor: "#C4845A",
  },
  {
    id: "prod-7",
    name: "Macetero Orgánico Canario",
    category: "Decoración",
    price: 28,
    priceUnit: "€",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff0db6c1-1772803197576.png",
    alt: "Macetero con formas orgánicas y textura de piedra volcánica impreso en PLA verde",
    description: "Macetero con diseño orgánico inspirado en las formas volcánicas de Lanzarote. Resistente al agua.",
    tags: ["PLA", "Resistente agua", "Eco"],
  },
  {
    id: "prod-8",
    name: "Render Fotorrealista 4K",
    category: "Arquitectura",
    price: 180,
    priceUnit: "€",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b63570d-1772803191004.png",
    alt: "Render fotorrealista de interior de vivienda con luz natural y mobiliario moderno",
    description: "Visualización fotorrealista de tu proyecto. 3 vistas exteriores o interiores en resolución 4K.",
    tags: ["4K", "3 vistas", "Entrega 72h"],
    badge: "Popular",
    badgeColor: "#C4845A",
  },
];

const categories = ["Todos", "Decoración", "Arquitectura", "Arte", "Personalizados"];

export default function ProductsInteractive() {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const filtered =
    activeCategory === "Todos"
      ? products
      : products.filter((p) => p.category === activeCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const items = headerRef.current?.querySelectorAll(".reveal");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setVisibleIds(new Set());
    filtered.forEach((p, i) => {
      setTimeout(() => {
        setVisibleIds((prev) => new Set([...prev, p.id]));
      }, i * 80 + 100);
    });
  }, [activeCategory]);

  return (
    <div className="min-h-screen bg-evolvia-bg">
      {/* Page Header */}
      <div
        ref={headerRef}
        className="relative pt-32 pb-20 px-6 lg:px-10 overflow-hidden bg-evolvia-bg"
      >
        {/* Background */}
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-evolvia-copper/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="reveal">
              {/* Logo */}
              <div className="mb-6">
                <Image
                  src="/assets/images/EVOLVIA_LOGO-1772803973696.png"
                  alt="Evolvia 3D logo"
                  width={140}
                  height={40}
                  className="object-contain h-10 w-auto opacity-60"
                />
              </div>
              <div className="inline-flex items-center gap-2 border border-evolvia-border rounded-sm px-4 py-2 mb-6">
                <div className="w-1.5 h-1.5 rounded-full bg-evolvia-copper" />
                <span className="text-xs font-700 uppercase tracking-[0.2em] text-evolvia-text-secondary">
                  Catálogo Evolvia3D
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black leading-[0.9] tracking-tight text-evolvia-text-primary mb-4">
                Nuestros
                <br />
                <span className="gradient-text-copper">Productos</span>
              </h1>
              <p className="text-evolvia-text-secondary text-lg max-w-md leading-relaxed">
                Diseños únicos, impresión premium y servicios profesionales.
                Envíos a toda Canarias en 5–10 días laborables.
              </p>
            </div>

            {/* Stats */}
            <div className="reveal delay-200 flex gap-4 flex-wrap">
              <div className="text-center px-6 py-4 border border-evolvia-border rounded-sm">
                <p className="text-3xl font-black gradient-text-copper">{products.length}+</p>
                <p className="text-xs text-evolvia-text-muted uppercase tracking-wider mt-1">Productos</p>
              </div>
              <div className="text-center px-6 py-4 border border-evolvia-border rounded-sm">
                <p className="text-3xl font-black text-evolvia-copper">€28</p>
                <p className="text-xs text-evolvia-text-muted uppercase tracking-wider mt-1">Desde</p>
              </div>
              <div className="text-center px-6 py-4 border border-evolvia-border rounded-sm">
                <p className="text-3xl font-black text-evolvia-text-primary">5d</p>
                <p className="text-xs text-evolvia-text-muted uppercase tracking-wider mt-1">Entrega</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sticky top-20 z-30 px-6 lg:px-10 py-4 bg-evolvia-bg/95 backdrop-blur-2xl border-b border-evolvia-border">
        <div className="max-w-7xl mx-auto flex items-center gap-3 overflow-x-auto scrollbar-hide pb-1">
          <span className="text-xs text-evolvia-text-muted uppercase tracking-widest font-600 shrink-0 mr-2">
            Filtrar:
          </span>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`filter-btn shrink-0 ${activeCategory === cat ? "active" : ""}`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto shrink-0 text-xs text-evolvia-text-muted">
            {filtered.length} producto{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Product Grid */}
      <div ref={ref} className="px-6 lg:px-10 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((product) => (
              <div
                key={`${product.id}-${activeCategory}`}
                className="product-card group cursor-pointer"
                style={{
                  opacity: visibleIds.has(product.id) ? 1 : 0,
                  transform: visibleIds.has(product.id) ? "translateY(0)" : "translateY(24px)",
                  transition:
                    "opacity 0.5s cubic-bezier(0.23,1,0.32,1), transform 0.5s cubic-bezier(0.23,1,0.32,1)",
                }}
              >
                {/* Image container */}
                <div className="relative h-56 overflow-hidden bg-evolvia-card">
                  <AppImage
                    src={product.image}
                    alt={product.alt}
                    fill
                    className="product-img object-cover"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-evolvia-card/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

                  {/* Badge */}
                  {product.badge && (
                    <div
                      className="absolute top-3 left-3 px-2.5 py-1 rounded-sm text-xs font-700 uppercase tracking-wider"
                      style={{
                        background: `${product.badgeColor}20`,
                        border: `1px solid ${product.badgeColor}40`,
                        color: product.badgeColor,
                      }}
                    >
                      {product.badge}
                    </div>
                  )}

                  {/* Quick action on hover */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                    <a
                      href="mailto:hola@evolvia3d.es"
                      className="btn-primary text-xs py-2.5 px-5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Solicitar
                    </a>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Category */}
                  <span className="tag-pill mb-3 inline-block">{product.category}</span>

                  {/* Name */}
                  <h3 className="text-lg font-700 text-evolvia-text-primary mb-2 leading-tight group-hover:text-evolvia-copper transition-colors duration-300">
                    {product.name}
                  </h3>

                  {/* Description */}
                  <p className="text-xs text-evolvia-text-secondary leading-relaxed mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {product.tags.map((tag) => (
                      <span
                        key={`${product.id}-${tag}`}
                        className="text-xs px-2 py-0.5 rounded-sm text-evolvia-text-muted"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Price + CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-evolvia-border">
                    <div>
                      {product.price === 0 ? (
                        <span className="text-sm text-evolvia-text-secondary font-500">Precio a consultar</span>
                      ) : (
                        <div className="flex items-baseline gap-1">
                          {product.priceUnit === "€ desde" && (
                            <span className="text-xs text-evolvia-text-muted">desde</span>
                          )}
                          <span className="text-2xl font-black gradient-text-copper">
                            {product.price}€
                          </span>
                        </div>
                      )}
                    </div>
                    <a
                      href="mailto:hola@evolvia3d.es"
                      className="w-9 h-9 rounded-sm flex items-center justify-center transition-all duration-300 hover:scale-110 bg-evolvia-copper/10 border border-evolvia-copper/25 hover:bg-evolvia-copper hover:border-evolvia-copper"
                      aria-label={`Solicitar ${product.name}`}
                    >
                      <svg className="w-4 h-4 text-evolvia-copper group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="text-center py-24">
              <p className="text-evolvia-text-muted text-lg">No hay productos en esta categoría aún.</p>
            </div>
          )}
        </div>
      </div>

      {/* Custom Order CTA Banner */}
      <div className="px-6 lg:px-10 pb-24">
        <div className="max-w-7xl mx-auto">
          <div
            className="relative rounded-sm overflow-hidden p-12 md:p-16 text-center border border-evolvia-border"
            style={{
              background: "linear-gradient(135deg, rgba(196,132,90,0.08) 0%, rgba(196,132,90,0.04) 100%)",
            }}
          >
            {/* Geometric accent */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-evolvia-copper/40 to-transparent" />

            <div className="relative z-10">
              <span className="tag-pill mb-6 inline-block">Proyecto a medida</span>
              <h2 className="text-4xl md:text-5xl font-black leading-[0.92] text-evolvia-text-primary mb-5">
                ¿No encuentras lo que buscas?
                <br />
                <span className="gradient-text-copper">Lo creamos para ti.</span>
              </h2>
              <p className="text-evolvia-text-secondary max-w-lg mx-auto mb-8 leading-relaxed">
                Diseñamos y fabricamos piezas únicas bajo pedido. Cuéntanos tu idea y recibirás presupuesto sin compromiso en 24 horas.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="mailto:hola@evolvia3d.es" className="btn-primary">
                  Pedir presupuesto gratis
                </a>
                <a href="tel:+34600000000" className="btn-outline">
                  Llamar ahora
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
